import type { ProductMarginRow, ProductMarginSalesByYear } from '@erp/rpc';
import { formatCurrency } from '@/components/common/Currency';

// Most recent N years to show in the stacked ASP / Qty cells. The margin
// query covers 4 years; we show the 3 most recent so each row stays compact
// and there's always a prior year to diff against.
const COMPACT_MAX_YEARS = 3;

function formatPctDelta(curr: number, prev: number | null): string | null {
  if (prev == null || prev === 0 || !Number.isFinite(prev)) return null;
  const pct = ((curr - prev) / prev) * 100;
  if (!Number.isFinite(pct)) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function formatIntDelta(curr: number, prev: number | null): string | null {
  if (prev == null) return null;
  const diff = curr - prev;
  if (diff === 0) return null;
  const sign = diff > 0 ? '+' : '';
  return `${sign}${diff.toLocaleString()}`;
}

function deltaColor(value: string | null): string {
  if (!value) return 'text-muted-foreground';
  if (value.startsWith('+')) return 'text-green-700 dark:text-green-400';
  if (value.startsWith('-')) return 'text-red-700 dark:text-red-400';
  return 'text-muted-foreground';
}

/**
 * One stacked history column: the most recent years (descending), each with
 * its delta vs. the next-older year in parentheses. `entries` is a
 * `salesByYear`-shaped series — regional or customer-only — so the Margin tab
 * can show both scopes with the same cell; absent or empty reads as "—".
 */
function StackedYoYCell({
  entries,
  formatValue,
  formatDelta,
}: {
  entries: ProductMarginSalesByYear[] | undefined;
  formatValue: (entry: ProductMarginSalesByYear) => string;
  formatDelta: (
    entry: ProductMarginSalesByYear,
    prev: ProductMarginSalesByYear | undefined,
  ) => string | null;
}) {
  const items = entries?.slice(0, COMPACT_MAX_YEARS) ?? [];
  if (items.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap text-xs leading-tight">
      {items.map((entry, idx) => {
        const delta = formatDelta(entry, items[idx + 1]);
        return (
          <div key={entry.year}>
            <span className="font-medium">{entry.year}:</span>{' '}
            {formatValue(entry)}
            {delta && (
              <span className={`ml-1 ${deltaColor(delta)}`}>({delta})</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Quantity-weighted ASP per year with the year-over-year % change. */
function formatAsp(entry: ProductMarginSalesByYear): string {
  return formatCurrency(entry.aspWeighted, { currency: 'USD' });
}
function aspDelta(
  entry: ProductMarginSalesByYear,
  prev: ProductMarginSalesByYear | undefined,
): string | null {
  return formatPctDelta(
    Number(entry.aspWeighted),
    prev ? Number(prev.aspWeighted) : null,
  );
}

/** Units sold per year with the year-over-year absolute change. */
function formatQty(entry: ProductMarginSalesByYear): string {
  return entry.qtySold.toLocaleString();
}
function qtyDelta(
  entry: ProductMarginSalesByYear,
  prev: ProductMarginSalesByYear | undefined,
): string | null {
  return formatIntDelta(entry.qtySold, prev?.qtySold ?? null);
}

/** Stacked ASP history for one series (regional or customer-only). */
export function AspHistoryCell({
  entries,
}: {
  entries: ProductMarginSalesByYear[] | undefined;
}) {
  return (
    <StackedYoYCell
      entries={entries}
      formatValue={formatAsp}
      formatDelta={aspDelta}
    />
  );
}

/** Stacked quantity-sold history for one series (regional or customer-only). */
export function QtyHistoryCell({
  entries,
}: {
  entries: ProductMarginSalesByYear[] | undefined;
}) {
  return (
    <StackedYoYCell
      entries={entries}
      formatValue={formatQty}
      formatDelta={qtyDelta}
    />
  );
}

/** Quote Builder cell: the row's regional ASP history. */
export function StackedAspCell({ row }: { row: ProductMarginRow | undefined }) {
  return <AspHistoryCell entries={row?.salesByYear} />;
}

/** Quote Builder cell: the row's regional quantity-sold history. */
export function StackedQtyCell({ row }: { row: ProductMarginRow | undefined }) {
  return <QtyHistoryCell entries={row?.salesByYear} />;
}
